import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IndianRupee, ReceiptText, FileWarning, Landmark, Download, Eye, AlertCircle,
} from 'lucide-react';
import { accountsAPI, EarningsResponse } from '../../services/api';
import { currentFY, fyOptions, FY_MONTHS } from './fy';
import { exportEarningsPdf } from './earningsPdf';
import { useAuth } from '../../contexts/AuthContext';

const inr = (n: number) =>
  n.toLocaleString('en-IN', { maximumFractionDigits: 2 });

const dmy = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const PENDING_COPY = 'Receipt not generated / Payment not received';

const EarningsTab: React.FC = () => {
  const { company } = useAuth();
  const [fy, setFy] = useState(currentFY());
  const [month, setMonth] = useState<number | ''>('');
  const [data, setData] = useState<EarningsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await accountsAPI.getEarnings(fy, month === '' ? null : month);
      setData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load earnings');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fy, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rows = data?.rows ?? [];
  const summary = data?.summary;

  const summaryCards = summary ? [
    { label: 'Total Earnings', value: `₹${inr(summary.totalAmount)}`, icon: IndianRupee, tone: 'from-indigo-600 to-purple-600' },
    { label: 'Tax Collected', value: `₹${inr(summary.totalTax)}`, icon: Landmark, tone: 'from-emerald-500 to-teal-600' },
    { label: 'TDS Deducted', value: `₹${inr(summary.totalTds)}`, icon: ReceiptText, tone: 'from-sky-500 to-indigo-500' },
    {
      label: 'Pending Receipts', value: String(summary.pendingReceipts), icon: FileWarning,
      tone: summary.pendingReceipts > 0 ? 'from-amber-500 to-orange-500' : 'from-slate-400 to-slate-500',
    },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Filters + export */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={fy}
          onChange={(e) => setFy(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {fyOptions().map((o) => <option key={o} value={o}>FY {o}</option>)}
        </select>
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
          className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All months</option>
          {FY_MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <button
          onClick={() => data && exportEarningsPdf(data, company?.companyName)}
          disabled={!data || rows.length === 0}
          className="ml-auto flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow-lg shadow-indigo-500/30 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Download className="h-4 w-4" />
          <span>Export PDF</span>
        </button>
      </div>

      {error && (
        <div className="flex items-center space-x-2 bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center space-x-3 min-w-0">
              <div className={`shrink-0 p-2.5 rounded-xl bg-gradient-to-br ${c.tone} shadow-md`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide truncate">{c.label}</div>
                <div className="text-lg lg:text-xl font-bold text-slate-900 truncate">{c.value}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-16 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-3 border-indigo-200 border-t-indigo-600" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center text-slate-500">
            No invoices or receipts found for FY {fy}{month !== '' ? ` — ${FY_MONTHS.find((m) => m.value === month)?.label}` : ''}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  <th className="px-4 py-3 whitespace-nowrap">Invoice No.</th>
                  <th className="px-4 py-3 whitespace-nowrap">Invoice Date</th>
                  <th className="px-4 py-3 whitespace-nowrap">Receipt Number</th>
                  <th className="px-4 py-3 whitespace-nowrap">Receipt Date</th>
                  <th className="px-4 py-3 whitespace-nowrap">Client Name</th>
                  <th className="px-4 py-3 whitespace-nowrap">GST Details</th>
                  <th className="px-4 py-3 whitespace-nowrap text-right">Taxable Amount</th>
                  <th className="px-4 py-3 whitespace-nowrap text-right">Tax Amount</th>
                  <th className="px-4 py-3 whitespace-nowrap text-right">TDS Deducted</th>
                  <th className="px-4 py-3 whitespace-nowrap text-right">Total Amount</th>
                  <th className="px-4 py-3 whitespace-nowrap">Invoice Link</th>
                  <th className="px-4 py-3 whitespace-nowrap">Receipt Link</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(() => {
                  const seen = new Set<string>();
                  return rows.map((r, i) => {
                    const isRepeat = r.invoiceId !== null && seen.has(r.invoiceId);
                    if (r.invoiceId !== null) seen.add(r.invoiceId);
                    return (
                  <tr key={`${r.invoiceId ?? r.invoiceNumber}-${r.receiptId ?? 'none'}-${i}`} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">{r.invoiceNumber}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{dmy(r.invoiceDate) || '—'}</td>
                    {r.receiptNumber ? (
                      <>
                        <td className="px-4 py-3 text-slate-900 whitespace-nowrap">{r.receiptNumber}</td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{dmy(r.receiptDate)}</td>
                      </>
                    ) : (
                      <td colSpan={2} className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium">
                          {PENDING_COPY}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3 text-slate-900 whitespace-nowrap">{r.clientName}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.clientGstin || '—'}</td>
                    <td className="px-4 py-3 text-right text-slate-900 whitespace-nowrap">{isRepeat ? '—' : `₹${inr(r.taxableAmount)}`}</td>
                    <td className="px-4 py-3 text-right text-slate-900 whitespace-nowrap">{isRepeat ? '—' : `₹${inr(r.taxAmount)}`}</td>
                    <td className="px-4 py-3 text-right text-slate-900 whitespace-nowrap">{r.tdsDeducted ? `₹${inr(r.tdsDeducted)}` : '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900 whitespace-nowrap">{isRepeat ? '—' : `₹${inr(r.totalAmount)}`}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.invoiceId ? (
                        <Link to={`/invoice/${r.invoiceId}`} className="inline-flex items-center space-x-1 text-indigo-600 hover:text-indigo-800 font-medium">
                          <Eye className="h-4 w-4" /><span>View</span>
                        </Link>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.receiptId ? (
                        <Link to={`/receipt/${r.receiptId}`} className="inline-flex items-center space-x-1 text-indigo-600 hover:text-indigo-800 font-medium">
                          <Eye className="h-4 w-4" /><span>View</span>
                        </Link>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                    );
                  });
                })()}
              </tbody>
              {summary && (
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold text-slate-900">
                    <td className="px-4 py-3" colSpan={6}>
                      Totals ({summary.totalInvoices} invoices, {summary.totalReceipts} receipts, {summary.pendingReceipts} pending)
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">₹{inr(summary.totalTaxable)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">₹{inr(summary.totalTax)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">₹{inr(summary.totalTds)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">₹{inr(summary.totalAmount)}</td>
                    <td className="px-4 py-3" colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EarningsTab;
